import { useState } from "react";
import { Pencil, Trash2 } from "lucide-react";

import BottomSheet from "./BottomSheet";
import {
  MiniAvatar,
  PosterSlot,
  StarRating,
  StatusBadge,
  TypeBadge,
} from "./EntryVisuals";
import {
  avatarColor,
  formatEntryDate,
  getAddedByLabel,
  getExtraText,
} from "../utils/helpers";
import { TOKENS as T } from "../styles/styles";

// Single implementation of the Figma entry detail sheet, reused by Tracker and
// Search.
export default function EntryDetailSheet({
  entry,
  currentUser,
  onClose,
  onEditEntry,
  onRemoveEntry,
}) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  if (!entry) return null;

  const addedByLabel = getAddedByLabel(entry, currentUser?.uid);
  const extraText = getExtraText(entry);
  const date = formatEntryDate(entry.createdAt);
  const tags = entry.tags || [];

  function handleClose() {
    setConfirmingDelete(false);
    onClose();
  }

  return (
    <BottomSheet onClose={handleClose}>
      <div style={headerStyle}>
        <PosterSlot type={entry.type} width={72} height={104} radius={16} />

        <div style={headerBodyStyle}>
          <TypeBadge type={entry.type} />

          <h2 style={titleStyle}>{entry.title}</h2>

          <StarRating rating={entry.rating} />

          <div style={{ marginTop: 8 }}>
            <StatusBadge entry={entry} />
          </div>
        </div>
      </div>

      <div style={dividerStyle} />

      <div style={sectionsStyle}>
        <div>
          <p style={sectionLabelStyle}>Added by</p>
          <div style={addedByRowStyle}>
            <MiniAvatar
              initial={(addedByLabel[0] || "?").toUpperCase()}
              color={avatarColor(entry.addedBy?.uid || addedByLabel)}
            />
            <span style={addedByNameStyle}>{addedByLabel}</span>
            {date && <span style={addedByDateStyle}>· {date}</span>}
          </div>
        </div>

        {extraText && (
          <div>
            <p style={sectionLabelStyle}>Details</p>
            <p style={bodyTextStyle}>{extraText}</p>
          </div>
        )}

        {tags.length > 0 && (
          <div>
            <p style={sectionLabelStyle}>Tags</p>
            <div style={tagsRowStyle}>
              {tags.map((tag) => (
                <span key={tag} style={tagStyle}>
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {entry.note && (
          <div>
            <p style={sectionLabelStyle}>Notes</p>
            <p style={bodyTextStyle}>{entry.note}</p>
          </div>
        )}

        {confirmingDelete ? (
          <div style={confirmBoxStyle}>
            <p style={confirmTitleStyle}>Remove “{entry.title}”?</p>
            <p style={confirmTextStyle}>
              It disappears for everyone in the group. This can't be undone.
            </p>

            <div style={actionsStyle}>
              <button
                type="button"
                style={cancelButtonStyle}
                onClick={() => setConfirmingDelete(false)}
              >
                Keep it
              </button>

              <button
                type="button"
                style={confirmDeleteButtonStyle}
                onClick={() => {
                  handleClose();
                  onRemoveEntry(entry.id);
                }}
              >
                Remove
              </button>
            </div>
          </div>
        ) : (
          <div style={actionsStyle}>
            <button
              type="button"
              style={editButtonStyle}
              onClick={() => {
                handleClose();
                onEditEntry(entry);
              }}
            >
              <Pencil size={16} />
              Edit
            </button>

            <button
              type="button"
              style={deleteButtonStyle}
              aria-label="Remove entry"
              onClick={() => setConfirmingDelete(true)}
            >
              <Trash2 size={20} color={T.destructive} />
            </button>
          </div>
        )}
      </div>
    </BottomSheet>
  );
}

const headerStyle = {
  display: "flex",
  gap: 16,
  padding: "12px 0 20px",
};

const headerBodyStyle = {
  flex: 1,
  minWidth: 0,
};

const titleStyle = {
  margin: "8px 0",
  fontSize: 20,
  fontWeight: 700,
  lineHeight: 1.25,
  color: T.foreground,
};

const dividerStyle = {
  height: 1,
  background: T.border,
  marginBottom: 20,
};

const sectionsStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 20,
};

const sectionLabelStyle = {
  margin: "0 0 8px",
  fontSize: 10,
  fontWeight: 600,
  color: T.mutedForeground,
  textTransform: "uppercase",
  letterSpacing: "0.1em",
};

const addedByRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const addedByNameStyle = {
  fontSize: 14,
  fontWeight: 600,
  color: T.foreground,
};

const addedByDateStyle = {
  fontSize: 12,
  color: T.mutedForeground,
};

const bodyTextStyle = {
  margin: 0,
  fontSize: 14,
  lineHeight: 1.6,
  color: T.foreground,
};

const tagsRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
};

const tagStyle = {
  padding: "4px 10px",
  borderRadius: 999,
  background: T.muted,
  fontSize: 12,
  fontWeight: 500,
  color: T.foreground,
};

const actionsStyle = {
  display: "flex",
  gap: 8,
  paddingTop: 4,
};

const editButtonStyle = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  padding: "14px 16px",
  borderRadius: 16,
  border: "none",
  background: T.primary,
  color: T.primaryForeground,
  fontSize: 14,
  fontWeight: 700,
  fontFamily: "inherit",
  cursor: "pointer",
};

const deleteButtonStyle = {
  width: 56,
  minHeight: 48,
  borderRadius: 16,
  border: "none",
  background: "rgba(229,55,58,0.10)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};

const confirmBoxStyle = {
  background: "rgba(229,55,58,0.06)",
  border: `1px solid rgba(229,55,58,0.18)`,
  borderRadius: 20,
  padding: 16,
};

const confirmTitleStyle = {
  margin: 0,
  fontSize: 14,
  fontWeight: 700,
  color: T.foreground,
};

const confirmTextStyle = {
  margin: "4px 0 14px",
  fontSize: 13,
  lineHeight: 1.5,
  color: T.mutedForeground,
};

const cancelButtonStyle = {
  flex: 1,
  minHeight: 48,
  padding: "14px 16px",
  borderRadius: 16,
  border: `1px solid ${T.border}`,
  background: T.card,
  color: T.foreground,
  fontSize: 14,
  fontWeight: 700,
  fontFamily: "inherit",
  cursor: "pointer",
};

const confirmDeleteButtonStyle = {
  flex: 1,
  minHeight: 48,
  padding: "14px 16px",
  borderRadius: 16,
  border: "none",
  background: T.destructive,
  color: "#fff",
  fontSize: 14,
  fontWeight: 700,
  fontFamily: "inherit",
  cursor: "pointer",
};
